import React, { useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { API } from '@/utils/api';
import { VIEWS, DAMAGE_TYPES, ViewShape, Markers, typeColor } from './CarShapes';

const LAYOUT = { gauche: [150, 0], avant: [0, 140], dessus: [150, 132], arriere: [460, 140], droite: [150, 272] };
const SIZE = { avant: [140, 114], arriere: [140, 114] };

export const CombinedMap = ({ damages = [] }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 430" width="600" height="430" style={{ width: '100%', height: 'auto' }}>
    <rect width="600" height="430" fill="#fff" />
    {VIEWS.map(v => {
      const [x, y] = LAYOUT[v.key]; const [w, h] = SIZE[v.key] || [v.w, v.h];
      return <svg key={v.key} x={x} y={y} width={w} height={h} viewBox={`0 0 ${v.w} ${v.h}`}><ViewShape view={v} /><Markers view={v} damages={damages} /></svg>;
    })}
    {[['Côté gauche', 70, 64], ['Côté droit', 70, 336], ['Avant', 70, 270], ['Arrière', 530, 270]].map(([t, x, y]) =>
      <text key={t} x={x} y={y} textAnchor="middle" fontSize="13" fontWeight="bold" fill="#475569" fontFamily="Arial">{t}</text>)}
    {DAMAGE_TYPES.map((t, i) => <g key={t.key} transform={`translate(${150 + i * 85} 414)`}><circle r="6" fill={t.color} />
      <text x="10" y="4" fontSize="12" fill="#334155" fontFamily="Arial">{t.label}</text></g>)}
  </svg>
);

export const damageMapToPng = (damages) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement('canvas'); c.width = 1200; c.height = 860;
    const ctx = c.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0, c.width, c.height);
    resolve(c.toDataURL('image/png'));
  };
  img.onerror = () => resolve('');
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(renderToStaticMarkup(<CombinedMap damages={damages} />))}`;
});

const DamageRow = ({ d, i, woId, selected, onSelect, onChange, onRemove }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append('file', f, `degat_${i + 1}_${Date.now()}_${f.name}`);
    try { const r = await axios.post(`${API}/work-orders/${woId}/attachments`, fd); onChange({ photo_id: r.data.id, preview: URL.createObjectURL(f) }); }
    catch { toast.error('Échec de l\'envoi de la photo'); } finally { setBusy(false); }
  };
  return (
    <div onClick={onSelect} className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${selected ? 'border-slate-900 bg-slate-50' : 'border-slate-200'}`} data-testid={`damage-row-${i}`}>
      <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: typeColor(d.type) }}>{i + 1}</span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-slate-500">{VIEWS.find(v => v.key === d.view)?.label}</p>
        <div className="flex gap-1 flex-wrap">{DAMAGE_TYPES.map(t => (
          <button key={t.key} type="button" onClick={() => onChange({ type: t.key })} data-testid={`damage-${i}-type-${t.key}`}
            className={`px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors ${d.type === t.key ? 'text-white' : 'bg-white text-slate-600'}`}
            style={d.type === t.key ? { background: t.color, borderColor: t.color } : undefined}>{t.label}</button>))}</div>
      </div>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid={`damage-${i}-photo-input`} />
      <button type="button" onClick={() => ref.current.click()} disabled={busy || !woId} data-testid={`damage-${i}-photo-btn`}
        className="w-12 h-12 rounded-md border-2 border-dashed flex items-center justify-center text-slate-500 overflow-hidden shrink-0 hover:border-slate-900">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : d.preview ? <img src={d.preview} alt="dégât" className="w-full h-full object-cover" /> : <Camera className="w-4 h-4" />}
      </button>
      <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="p-1 text-slate-400 hover:text-red-600" data-testid={`damage-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
};

export const DamageMapEditor = ({ woId, value, onChange }) => {
  const [viewKey, setViewKey] = useState('gauche');
  const [selected, setSelected] = useState(null);
  const view = VIEWS.find(v => v.key === viewKey);
  const add = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const d = { view: viewKey, type: 'rayure', x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height, photo_id: '' };
    onChange([...value, d]); setSelected(value.length);
  };
  const update = (i, patch) => onChange(value.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  return (
    <div className="space-y-2" data-testid="damage-map-editor">
      <div className="flex gap-1 overflow-x-auto">{VIEWS.map(v => {
        const n = value.filter(d => d.view === v.key).length;
        return <button key={v.key} type="button" onClick={() => setViewKey(v.key)} data-testid={`damage-view-${v.key}`}
          className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border whitespace-nowrap transition-colors ${viewKey === v.key ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300'}`}>{v.label}{n > 0 && ` (${n})`}</button>;
      })}</div>
      <div className="rounded-xl border bg-white p-2">
        <svg viewBox={`0 0 ${view.w} ${view.h}`} className="w-full h-auto cursor-crosshair touch-manipulation" onClick={add} data-testid="damage-map-svg">
          <ViewShape view={view} /><Markers view={view} damages={value} selected={selected} />
        </svg>
        <p className="text-[11px] text-center text-slate-500">Touchez l'endroit du dégât sur le dessin</p>
      </div>
      {value.map((d, i) => <DamageRow key={i} d={d} i={i} woId={woId} selected={selected === i} onSelect={() => { setSelected(i); setViewKey(d.view); }}
        onChange={(p) => update(i, p)} onRemove={() => { onChange(value.filter((_, j) => j !== i)); setSelected(null); }} />)}
    </div>
  );
};
